import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Handshake, ChatCircleDots, PaperPlaneTilt, Camera, Receipt, CheckSquare, Circle, ClockCounterClockwise,
} from "@phosphor-icons/react";

const KIND_META = {
  meeting:                { icon: ChatCircleDots, label: "Meeting" },
  stakeholder_digest:     { icon: PaperPlaneTilt, label: "Stakeholder digest" },
  snapshot:               { icon: Camera,          label: "Snapshot captured" },
  invoice:                { icon: Receipt,         label: "Invoice" },
  task_completed:         { icon: CheckSquare,     label: "Task completed" },
  engagement_created:     { icon: Handshake,       label: "Engagement began" },
  engagement_status_change:{ icon: Circle,         label: "Engagement status" },
};

const TONE_ACCENT = {
  primary: "border-primary/60 text-primary",
  success: "border-emerald-500/60 text-emerald-500",
  accent:  "border-amber-500/60 text-amber-500",
  muted:   "border-border text-muted-foreground",
};

function fmtDate(iso) {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y) return s;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(m,10)-1] || m} ${parseInt(d,10)}, ${y}`;
}

/**
 * Unified relationship arc — every meeting, digest, snapshot, invoice, and
 * completed task on this client or engagement, sorted newest-first on a
 * vertical spine. Named entities receive a one-line summary + kind pill.
 */
export default function RelationshipTimeline({ clientId, engagementId }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId && !engagementId) return;
    setLoading(true);
    const path = engagementId ? `/timeline/engagement/${engagementId}` : `/timeline/client/${clientId}`;
    api.get(path)
      .then((r) => setEvents(r.data.events || []))
      .catch(() => toast.error("Could not load timeline"))
      .finally(() => setLoading(false));
  }, [clientId, engagementId]);

  if (loading) {
    return (
      <div className="border border-border bg-card p-6" data-testid="timeline-loading">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary flex items-center gap-1.5">
          <ClockCounterClockwise size={12} weight="fill" /> Relationship timeline
        </div>
        <div className="text-xs text-muted-foreground mt-3">Assembling the arc…</div>
      </div>
    );
  }
  if (!events || events.length === 0) {
    return (
      <div className="border border-border bg-card p-6" data-testid="timeline-empty">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary flex items-center gap-1.5">
          <ClockCounterClockwise size={12} weight="fill" /> Relationship timeline
        </div>
        <div className="text-sm italic text-muted-foreground mt-3">No history on record yet — every meeting, digest, snapshot, invoice, and completed task will land here.</div>
      </div>
    );
  }

  return (
    <div className="border border-border bg-card p-6" data-testid="timeline">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary flex items-center gap-1.5">
            <ClockCounterClockwise size={12} weight="fill" /> Relationship timeline
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {events.length} touchpoint{events.length === 1 ? "" : "s"} · newest first
          </div>
        </div>
      </div>

      <div className="relative pl-6 border-l-2 border-border/60" data-testid="timeline-spine">
        {events.map((ev, i) => {
          const meta = KIND_META[ev.kind] || { icon: Circle, label: ev.kind };
          const Icon = meta.icon;
          const tone = TONE_ACCENT[ev.tone] || TONE_ACCENT.muted;
          return (
            <div key={`${ev.kind}-${ev.at}-${i}`} className="relative mb-5" data-testid={`timeline-event-${ev.kind}`}>
              {/* Spine node */}
              <div className={`absolute -left-[33px] top-0.5 h-6 w-6 border-2 bg-card flex items-center justify-center ${tone}`}>
                <Icon size={12} weight="bold" />
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{ev.title}</div>
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
                    {meta.label}
                  </div>
                </div>
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground shrink-0">{fmtDate(ev.at)}</div>
              </div>
              {ev.summary && (
                <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{ev.summary}</div>
              )}
              {ev.meta?.decisions > 0 && (
                <div className="text-[0.625rem] font-mono text-primary/80 mt-1">
                  {ev.meta.decisions} decision{ev.meta.decisions === 1 ? "" : "s"} · {ev.meta.action_items} action item{ev.meta.action_items === 1 ? "" : "s"}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
